import type { TestimonialsSection } from '@/data/types';

interface ClientTestimonialsProps {
  data: TestimonialsSection;
}

export function ClientTestimonials({ data }: ClientTestimonialsProps) {
  // Steve L. is the main testimonial (first in array)
  const mainTestimonial = data.testimonials[0];
  // Nicholas P. and Wilbur H. are the side testimonials
  const sideTestimonials = data.testimonials.slice(1);

  return (
    <section id="testimonial" className="py-16 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {data.title}
          </h2>
          {data.subtitle && (
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {data.subtitle}
            </p>
          )}
        </header>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Main testimonial (Steve L.) - Left side */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex justify-start mb-6">
              <svg className="w-12 h-12 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-10zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
              </svg>
            </div>
            
            <blockquote className="mb-6">
              <p className="text-gray-700 leading-relaxed text-base mb-4">
                {mainTestimonial.text}
              </p>
              <cite className="text-gray-900 font-semibold">
                - {mainTestimonial.name}
              </cite>
            </blockquote>
          </div>

          {/* Side testimonials (Nicholas P. and Wilbur H.) - Right side */}
          <div className="space-y-4">
            {sideTestimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className={`relative rounded-lg shadow-md p-6 text-white overflow-hidden ${
                  index === 0 ? 'bg-cyan-400' : 'bg-cyan-500'
                }`}
                style={{
                  backgroundImage: index === 1 ? 
                    "linear-gradient(rgba(6, 182, 212, 0.8), rgba(6, 182, 212, 0.8)), url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><defs><pattern id=\"grain\" patternUnits=\"userSpaceOnUse\" width=\"100\" height=\"100\"><image href=\"data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==\" width=\"100\" height=\"100\" opacity=\"0.1\"/></pattern></defs><rect width=\"100\" height=\"100\" fill=\"url(%23grain)\"/></svg>')" :
                    undefined
                }}
              >
                <div className="flex justify-start mb-4">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-10zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
                  </svg>
                </div>
                
                <blockquote>
                  <p className="text-white leading-relaxed text-sm mb-3">
                    {testimonial.text.length > 100 ? 
                      `${testimonial.text.substring(0, 100)}...` : 
                      testimonial.text
                    }
                  </p>
                  <cite className="text-white font-semibold text-sm">
                    - {testimonial.name}
                  </cite>
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}